import {CdkDragDrop} from '@angular/cdk/drag-drop';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {DraggingService} from '@application/services/dragging/dragging.service';
import {Coordinates} from '@domain/coordinates';
import {PositionOfDialog} from '@domain/position-of-dialog';
import {BmpProductionScheduleItemInPathGroup} from '@domain/production-schedule/bmp-production-schedule-item-in-path-group';
import {ProductionOrderCustomSettings} from '@domain/production-schedule/custom-settings/production-order-custom-settings';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {FreeZone} from '@domain/production-schedule/free-zone/free-zone';
import {ProductionSchedule} from '@domain/production-schedule/production-schedule';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {ProductionScheduleOrderLine} from '@domain/production-schedule/production-schedule-order-line';
import {ProductionSchedulePath} from '@domain/production-schedule/production-schedule-path';
import {ProductionSchedulePathsOfColoredYarnSet} from '@domain/production-schedule/production-schedule-paths-of-colored-yarn-set';
import {ProductionSchedulePhase} from '@domain/production-schedule/production-schedule-phase.enum';
import {RestZone} from '@domain/production-schedule/rest-zone/rest-zone';
import {PropertyValue} from '@domain/property-value';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {ListDrawing} from '@presentation/components/drawing-list/list-drawing';
import {RepositionDialogComponent} from '@presentation/components/reposition-dialog/reposition-dialog.component';
import {AssertionUtils, BaseComponent, Color, ColorConstants, convertToCommercialUnitCentimeter, DialogBuilderFactoryService, DialogType, DOMUtils, Unit} from '@vdw/angular-component-library';
import {select as d3Select, selectAll as d3SelectAll} from 'd3-selection';
import {cloneDeep} from 'lodash-es';
import {filter, takeUntil} from 'rxjs';
import {EditProductionScheduleItemInPathGroupAction} from '../../modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group-action';
import {EditProductionScheduleItemInPathGroupComponent} from '../../modals/edit-production-schedule-item-in-path-group/edit-production-schedule-item-in-path-group.component';
import {PathItemInfoTooltipContext} from '../../modals/path-item-info-tooltip/path-item-info-tooltip-context.enum';
import {PathItemInfoTooltipComponent} from '../../modals/path-item-info-tooltip/path-item-info-tooltip.component';
import {PRODUCTION_SCHEDULE_PLAN, ProductionSchedulePlan} from '../../production-schedule-plan';
import {MovePathItemParams} from './move-path-item-params.interface';

@Component({
  selector: 'app-path-bar-component',
  templateUrl: './path-bar.component.html',
  styleUrls: ['./path-bar.component.scss']
})
export class PathBarComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  @Input() public productionSchedule: ProductionSchedule;
  @Input() public productionSchedulePath: ProductionSchedulePath;
  @Input() public productionSchedulePathIndex: number;
  @Input() public productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public commercialUnit: Unit;
  @Input() public listOfCustomSettings: PropertyValue[];
  @Input() public productionOrderCustomSettings: ProductionOrderCustomSettings;
  @Input() public originalBuggyLengthMaxInPicks: number;
  @Input() public buggyLengthMaxInPicks: number;
  @Input() public availableSpaceInBuggyInPicks: number;
  @Input() public buggyLengthMinInPicks: number;
  @Input() public productionSchedulePhase: ProductionSchedulePhase;
  @Input() public selectedPath: ProductionSchedulePath;
  @Input() public selectedRestPathColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public minimumBuggyLengthIndicatorPlace: number;
  @Input() public maximumBuggyLengthIndicatorPlace: number;
  @Input() public isDraggingProductionScheduleItem: boolean;
  @Input() public isResizing: boolean;
  @Output() public minimumBuggyLengthIndicatorPlaceOutput = new EventEmitter<number>();
  @Output() public maximumBuggyLengthIndicatorPlaceOutput = new EventEmitter<number>();
  @Output() public isDraggingProductionScheduleItemOutput = new EventEmitter<boolean>();
  @Output() public hoverPathBarOutput = new EventEmitter<boolean>();
  @Output() public selectedPathOutput: EventEmitter<ProductionSchedulePath> = new EventEmitter<ProductionSchedulePath>();
  @Output() public selectedRestPathColoredYarnSetOutput: EventEmitter<OverviewListColoredYarnSetWithStartDent> = new EventEmitter<OverviewListColoredYarnSetWithStartDent>();

  @ViewChild('pathBar') public pathBar: ElementRef;
  @ViewChildren('itemsInPathGroup') public itemInPathGroupComponents: QueryList<ElementRef>;

  public suggestedRestZones: RestZone[];
  public widthOfPathItems: number[] = [];
  public suggestedFreeZones: FreeZone[];
  private productionScheduleItem: ProductionScheduleItemInPathGroup;
  private productionScheduleItemIndexInPath: number;
  private dialogRef: MatDialogRef<RepositionDialogComponent>;
  private editWindowOpen = false;
  private TOTAL_TO_DEDUCT_FROM_PATH_ITEM_FOR_PADDING_AND_GAPS = 0;

  private readonly VERTICAL_HOVER_OFFSET = 64;
  private readonly HORIZONTAL_HOVER_OFFSET = 64;
  private readonly MINIMAL_WIDTH_OF_PATH_BAR_ITEM = 8;
  private readonly DIALOG_WIDTH = 334;
  private readonly PADDING_AND_GAP_SIZE = 4;
  private readonly REPOSITION_DIALOG_CLASS = 'reposition-dialog';
  private readonly WEAVE_PLAN_TRANSLATION_KEY = 'PRODUCTION_ORDER.WEAVE_PLAN.WEAVE_PLAN';

  public constructor(
    @Inject(PRODUCTION_SCHEDULE_PLAN) private readonly productionSchedulePlan: ProductionSchedulePlan,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly draggingService: DraggingService,
    private readonly zone: NgZone,
    private readonly dialog: MatDialog
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setTotalToDeductFromPathItemForPaddingAndGaps();
    this.initialiseDropService();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      'buggyLengthMaxInPicks' in changes ||
      'availableSpaceInBuggyInPicks' in changes ||
      'productionSchedule' in changes ||
      'productionSchedulePath' in changes ||
      'originalBuggyLengthMaxInPicks' in changes
    ) {
      setTimeout(() => {
        this.resizePathBarComponent();
      });
    }
  }

  public ngAfterViewInit(): void {
    if (!AssertionUtils.isNullOrUndefined(this.pathBar)) {
      DOMUtils.observeResize(this.pathBar.nativeElement)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe(() => this.resizePathBarComponent());
    }
  }

  public ngOnDestroy(): void {
    this.dialogRef?.close();
  }

  public getPathItemStyle(pathItem: ProductionScheduleItemInPathGroup): object {
    if (pathItem.scheduleItemType === DrawingType.MANUAL_NON_PRODUCTION_ITEM) {
      return {'background-image': 'url("assets/images/jute-background.svg")'};
    } else {
      const rgbValues = this.productionScheduleColoredYarnSet?.coloredYarnSet?.overviewListColorSet?.creelPositions[pathItem.borderColorPosition - 1]?.items[0] as Color;
      return {'background-color': `rgb(${rgbValues?.rgb?.red}, ${rgbValues?.rgb?.green}, ${rgbValues?.rgb?.blue})`};
    }
  }

  public getPathBarBorderColor(): string {
    if (!AssertionUtils.isNullOrUndefined(this.productionSchedulePath) && this.selectedPath === this.productionSchedulePath) {
      return ColorConstants.PRIMARY_COLOR_VDW_BLUE;
    } else if (!AssertionUtils.isNullOrUndefined(this.selectedRestPathColoredYarnSet) && this.selectedRestPathColoredYarnSet.startDent === this.productionScheduleColoredYarnSet.startDent) {
      return ColorConstants.PRIMARY_COLOR_VDW_BLUE;
    } else {
      return ColorConstants.TINT_DARKBLUE_20;
    }
  }

  public canShowMinimumbuggyLengthIndicator(): boolean {
    return this.buggyLengthMaxInPicks !== this.buggyLengthMinInPicks && !AssertionUtils.isNullOrUndefined(this.buggyLengthMinInPicks);
  }

  public canShowMaxmimumbuggyLengthIndicator(): boolean {
    return this.buggyLengthMaxInPicks !== this.originalBuggyLengthMaxInPicks && !AssertionUtils.isNullOrUndefined(this.buggyLengthMaxInPicks);
  }

  public resizePathBarComponent(): void {
    this.setTotalToDeductFromPathItemForPaddingAndGaps();
    this.setWidthOfPathItems();
    this.setMinimumBuggyLengthIndicatorPlace();
    this.setMaximumBuggyLengthIndicatorPlace();
  }

  public draggingStopped(): void {
    this.isDraggingProductionScheduleItem = false;
    this.isDraggingProductionScheduleItemOutput.emit(this.isDraggingProductionScheduleItem);
  }

  public onRowDrag(productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup, productionScheduleItemInPathGroupIndex: number): void {
    this.isDraggingProductionScheduleItem = true;
    this.isDraggingProductionScheduleItemOutput.emit(this.isDraggingProductionScheduleItem);

    this.draggingService.setData({
      draggedItem: productionScheduleItemInPathGroup,
      sourcePath: this.productionSchedulePath,
      productionScheduleItemInPathGroupIndex,
      sourceColoredYarnSet: this.productionScheduleColoredYarnSet
    } as MovePathItemParams);
  }

  public openEditProductionScheduleItemDialog(event: MouseEvent, productionScheduleItem: ProductionScheduleItemInPathGroup, productionScheduleItemIndexInPath: number): void {
    this.productionScheduleItem = productionScheduleItem;
    this.productionScheduleItemIndexInPath = productionScheduleItemIndexInPath;
    this.editWindowOpen = true;

    this.dialogBuilderFactoryService
      .getBuilder()
      .withWidth('334px')
      .withClass(this.REPOSITION_DIALOG_CLASS)
      .withCustomBackdrop('transparent-backdrop')
      .openDialog(RepositionDialogComponent, {
        component: EditProductionScheduleItemInPathGroupComponent,
        sourceElement: event.target,
        positionOfDialog: PositionOfDialog.RIGHT,
        horizontalOffset: event.offsetX - (event.target as HTMLElement).clientWidth,
        productionScheduleItemInPathGroup: this.productionScheduleItem,
        commercialUnit: this.commercialUnit,
        productionSchedule: this.productionSchedule,
        allowExceedingRequestedAmountOfOrderLineItems: this.productionOrderCustomSettings?.allowExceedingRequestedAmountOfOrderLineItems,
        pickDensityInPicksPerMeter: this.productionSchedule.machineQuality.pickDensity * 1000
      })
      .pipe(filter((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => !AssertionUtils.isNullOrUndefined(result)))
      .subscribe((result: {action: EditProductionScheduleItemInPathGroupAction; value: ProductionScheduleItemInPathGroup}) => {
        if (result.action === EditProductionScheduleItemInPathGroupAction.EDIT) {
          this.editProductionScheduleItem(result.value);
        } else {
          this.removeProductionScheduleItemInPathGroup();
        }

        this.editWindowOpen = false;
      });
  }

  public closeDialogIfOpen(): void {
    if (!this.editWindowOpen) {
      this.dialog.closeAll();
    }
  }

  public openHoverInfoTooltip(event: MouseEvent, productionScheduleItem: ProductionScheduleItemInPathGroup, index: number): void {
    if (event.type === 'mouseup') {
      this.isResizing = false;
      this.hoverPathBarOutput.emit();
    }

    event.preventDefault();
    event.stopPropagation();

    if (this.isResizing) {
      return;
    }

    if (!this.isDraggingProductionScheduleItem) {
      this.zone.runOutsideAngular(() => {
        this.dialogRef = this.dialogBuilderFactoryService
          .getBuilder()
          .withWidth(`${this.DIALOG_WIDTH}px`)
          .withClass(this.REPOSITION_DIALOG_CLASS)
          .withCustomBackdrop('no-pointer-events-backdrop')
          .open(RepositionDialogComponent, {
            showArrow: false,
            event,
            sourceElement: event.target,
            component: PathItemInfoTooltipComponent,
            positionOfDialog: window.innerWidth - event.clientX > this.DIALOG_WIDTH ? PositionOfDialog.RIGHT : PositionOfDialog.LEFT,
            verticalOffset: this.VERTICAL_HOVER_OFFSET,
            horizontalOffset: this.HORIZONTAL_HOVER_OFFSET,
            moveWithCursor: true,
            context: PathItemInfoTooltipContext.PATH_BAR,
            number: index + 1,
            drawingName: productionScheduleItem.drawing?.name,
            image: productionScheduleItem.scheduleItemType === DrawingType.MANUAL_NON_PRODUCTION_ITEM ? {data: 'assets/images/jute-background.svg'} : productionScheduleItem.drawing?.image,
            finishingName: this.getFinishingName(productionScheduleItem),
            cutbarBefore: productionScheduleItem.getCutbarInformation()?.cutbarBefore?.commercialLengthInMM,
            cutbarAfter: productionScheduleItem.getCutbarInformation()?.cutbarAfter?.commercialLengthInMM,
            cutbarBeforeInPicks: productionScheduleItem.getCutbarInformation()?.cutbarBefore?.technicalLengthInNumberOfPicks,
            cutbarAfterInPicks: productionScheduleItem.getCutbarInformation()?.cutbarAfter?.technicalLengthInNumberOfPicks,
            quantity: this.productionSchedule.machine.jacquard.isSisal
              ? productionScheduleItem.quantity
              : productionScheduleItem.quantity * this.productionSchedule.getProductionScheduleItemMultiplier(),
            orderline: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.salesOrderLineReference ?? '',
            sales: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.salesOrderReference ?? '',
            dueDate: this.getOrderlineProperty(productionScheduleItem.orderLineId)?.desiredDate,
            width: convertToCommercialUnitCentimeter({
              unit: Unit.MILLIMETER,
              value: productionScheduleItem?.commercialDimensionsInMM?.widthInMM
            }),
            length: convertToCommercialUnitCentimeter({
              unit: Unit.MILLIMETER,
              value: productionScheduleItem?.commercialDimensionsInMM?.heightInMM
            })
          });
      });
    }
  }

  public moveDroppedDrawingToProductionSchedulePath(event: CdkDragDrop<any[]>): void {
    const dragProperties: MovePathItemParams = cloneDeep(this.draggingService.getCurrentData());
    if (
      AssertionUtils.isNullOrUndefined(this.productionSchedulePath) &&
      this.productionSchedule?.canMoveProductionScheduleItemToColoredYarnSet(dragProperties?.sourcePath?.pathNumber, this.productionScheduleColoredYarnSet?.startDent, dragProperties?.draggedItem)
    ) {
      this.productionSchedulePlan?.moveProductionScheduleItemToColoredYarnSet(
        dragProperties?.sourcePath?.pathNumber,
        dragProperties?.sourceColoredYarnSet,
        this.productionScheduleColoredYarnSet,
        dragProperties.draggedItem
      );
    } else if (
      (AssertionUtils.isNullOrUndefined(this.productionSchedulePath) &&
        !this.productionSchedule?.canMoveProductionScheduleItemToColoredYarnSet(
          dragProperties?.sourcePath?.pathNumber,
          this.productionScheduleColoredYarnSet?.startDent,
          dragProperties?.draggedItem
        )) ||
      (!AssertionUtils.isNullOrUndefined(this.productionSchedulePath) &&
        this.productionSchedulePath.pathNumber !== dragProperties?.sourcePath?.pathNumber &&
        !this.productionSchedule?.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(dragProperties?.draggedItem, this.productionSchedulePath?.technicalWidthInDents))
    ) {
      this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
        titleText: this.WEAVE_PLAN_TRANSLATION_KEY,
        messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
        type: DialogType.INFORMATION
      });
    } else if (this.productionSchedule?.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(dragProperties?.draggedItem, this.productionSchedulePath?.technicalWidthInDents)) {
      this.productionSchedulePlan?.moveProductionScheduleItemToProductionSchedulePath(
        dragProperties?.sourcePath?.pathNumber,
        dragProperties?.productionScheduleItemInPathGroupIndex,
        dragProperties?.sourceColoredYarnSet,
        this.productionSchedulePath.pathNumber,
        event.currentIndex,
        this.productionScheduleColoredYarnSet,
        dragProperties?.draggedItem
      );
    }
  }

  private setTotalToDeductFromPathItemForPaddingAndGaps(): void {
    this.TOTAL_TO_DEDUCT_FROM_PATH_ITEM_FOR_PADDING_AND_GAPS = this.PADDING_AND_GAP_SIZE - this.PADDING_AND_GAP_SIZE / this.productionSchedulePath?.pathGroups?.length;
  }

  private initialiseDropService(): void {
    d3Select(this.elementRef.nativeElement).datum(this.productionSchedulePath);
    this.zone.runOutsideAngular(() => {
      d3Select(this.elementRef.nativeElement)
        .on('drop', (event: DragEvent) => this.droppedNewDrawingOnProductionSchedulePath(event))
        .on('dragover', (event: DragEvent) => this.draggedNewDrawingOverProductionSchedulePath(event))
        .on('dragenter', (event: DragEvent) => event.stopPropagation())
        .on('dragleave', (event: DragEvent) => {
          event.stopPropagation();
          this.removeDropIndicator();
        });
    });
  }

  private droppedNewDrawingOnProductionSchedulePath(event: DragEvent): void {
    const draggedItem: ListDrawing = cloneDeep(this.draggingService.getCurrentData());

    if (!AssertionUtils.isNullOrUndefined(draggedItem) && !('quantity' in this.draggingService.getCurrentData())) {
      this.addDroppedDrawingToProductionSchedulePath(event, draggedItem);

      this.renderer.removeClass(this.elementRef.nativeElement.children[0], 'highlight');
      d3Select('.drawing-drag-preview').remove();

      this.removeDropIndicator();
    }
  }

  private draggedNewDrawingOverProductionSchedulePath(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const draggedItem: ListDrawing = this.draggingService.getCurrentData();

    if (!AssertionUtils.isNullOrUndefined(draggedItem) && !('quantity' in draggedItem)) {
      this.removeDropIndicator();
    }
  }

  private removeDropIndicator(): void {
    d3SelectAll('.production-schedule-drop-indicator').remove();
  }

  private addDroppedDrawingToProductionSchedulePath(event: DragEvent, draggedItem: ListDrawing): void {
    if (
      AssertionUtils.isNullOrUndefined(this.productionSchedulePath) &&
      this.productionSchedule.canAddProductionScheduleItemToColoredYarnSet(this.productionScheduleColoredYarnSet.startDent, draggedItem)
    ) {
      this.productionSchedulePlan
        .addDrawingToColoredYarnSet(draggedItem, this.productionScheduleColoredYarnSet.startDent)
        .pipe(takeUntil(this.unSubscribeOnViewDestroy))
        .subscribe((path: ProductionSchedulePath) => {
          if (!AssertionUtils.isNullOrUndefined(path)) {
            this.selectedPath = path;
            this.selectedRestPathColoredYarnSet = undefined;
            this.selectedPathOutput.emit(this.selectedPath);
            this.selectedRestPathColoredYarnSetOutput.emit(this.selectedRestPathColoredYarnSet);
          }
        });
    } else if (
      !AssertionUtils.isNullOrUndefined(this.productionSchedulePath) &&
      this.productionSchedule.canAddProductionScheduleItemToProductionSchedulePathForNewBuilder(draggedItem, this.productionSchedulePath?.technicalWidthInDents)
    ) {
      const productionSchedulePathsOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(this.productionScheduleColoredYarnSet.startDent);
      const productionScheduleItemsInPathGroup: ElementRef[] = this.itemInPathGroupComponents
        .toArray()
        .map((productionScheduleItemInPathGroup: any) => productionScheduleItemInPathGroup.nativeElement as ElementRef);

      const destinationIndexInProductionSchedulePath = this.getDropDestinationIndexInPath(this.getMouseCoordinatesInProductionSchedulePath(event), productionScheduleItemsInPathGroup);

      this.productionSchedulePlan.addDrawingToProductionSchedulePath(
        draggedItem,
        this.productionSchedulePath.pathNumber,
        productionSchedulePathsOfColoredYarnSet.productionScheduleColoredYarnSet,
        !AssertionUtils.isNullOrUndefined(destinationIndexInProductionSchedulePath) ? destinationIndexInProductionSchedulePath : null
      );
    } else {
      this.dialogBuilderFactoryService.getBuilder().openAlertDialog({
        titleText: this.WEAVE_PLAN_TRANSLATION_KEY,
        messageText: 'PRODUCTION_ORDER.WEAVE_PLAN.WARNINGS.PATH_WIDTH_DOES_NOT_EQUAL_DESIGN_WIDTH',
        type: DialogType.INFORMATION
      });
    }
  }

  private getDropDestinationIndexInPath(mouseCoordinates: Coordinates, productionScheduleItemsInPathGroup: ElementRef[]): number {
    let closestIndex: number | null = null;
    let closestDistance: number = Infinity;

    productionScheduleItemsInPathGroup.forEach((productionScheduleItemInPathGroup: any, index: number) => {
      const rect = productionScheduleItemInPathGroup.getBoundingClientRect();
      const distanceFromStart = Math.abs(mouseCoordinates.x - rect.left);
      const distanceFromEnd = Math.abs(mouseCoordinates.x - rect.right);

      if (distanceFromStart < closestDistance && distanceFromStart < distanceFromEnd) {
        closestDistance = distanceFromStart;
        closestIndex = index;
      } else if (distanceFromEnd < closestDistance) {
        closestDistance = distanceFromEnd;
        closestIndex = index + 1;
      }
    });

    return closestIndex;
  }

  private setMinimumBuggyLengthIndicatorPlace(): void {
    if (
      !AssertionUtils.isNullOrUndefined(this.pathBar?.nativeElement?.offsetWidth) &&
      !AssertionUtils.isNullOrUndefined(this.buggyLengthMaxInPicks) &&
      !AssertionUtils.isNullOrUndefined(this.buggyLengthMinInPicks)
    ) {
      const availableWindowSpace = this.pathBar?.nativeElement?.offsetWidth;
      const widthPerPixel = availableWindowSpace / this.buggyLengthMaxInPicks;

      this.minimumBuggyLengthIndicatorPlace = this.buggyLengthMinInPicks * widthPerPixel;
      this.minimumBuggyLengthIndicatorPlaceOutput.emit(this.minimumBuggyLengthIndicatorPlace);
    }
  }

  private getFinishingName(productionScheduleItem: ProductionScheduleItemInPathGroup): string {
    let name = '';

    if (!AssertionUtils.isNullOrUndefined(productionScheduleItem.orderline)) {
      name = productionScheduleItem.orderline?.article?.finishing?.nameWithVersion;
    } else if (!AssertionUtils.isNullOrUndefined(productionScheduleItem.drawing) && productionScheduleItem.drawing.drawingType === DrawingType.BMP) {
      const bmpProductionScheduleItem = productionScheduleItem as any as BmpProductionScheduleItemInPathGroup;
      name = bmpProductionScheduleItem?.finishing?.nameWithVersion;
    }

    return name;
  }

  private setMaximumBuggyLengthIndicatorPlace(): void {
    if (
      !AssertionUtils.isNullOrUndefined(this.pathBar?.nativeElement?.offsetWidth) &&
      !AssertionUtils.isNullOrUndefined(this.buggyLengthMaxInPicks) &&
      !AssertionUtils.isNullOrUndefined(this.originalBuggyLengthMaxInPicks) &&
      !AssertionUtils.isNullOrUndefined(this.productionSchedulePath)
    ) {
      const availableWindowSpace = this.pathBar?.nativeElement?.offsetWidth;
      const widthPerPixel = availableWindowSpace / this.buggyLengthMaxInPicks;

      this.maximumBuggyLengthIndicatorPlace = this.originalBuggyLengthMaxInPicks * widthPerPixel;
      this.maximumBuggyLengthIndicatorPlaceOutput.emit(this.maximumBuggyLengthIndicatorPlace);
    }
  }

  private getMouseCoordinatesInProductionSchedulePath(event: DragEvent): Coordinates {
    return {x: event.x, y: event.y};
  }

  private setWidthOfPathItems(): void {
    if (
      !AssertionUtils.isNullOrUndefined(this.pathBar?.nativeElement?.offsetWidth) &&
      !AssertionUtils.isNullOrUndefined(this.buggyLengthMaxInPicks) &&
      !AssertionUtils.isNullOrUndefined(this.productionSchedulePath)
    ) {
      const availableWindowSpace = this.pathBar?.nativeElement?.offsetWidth;
      let availableSpaceInBuggy: number;

      if (AssertionUtils.isNullOrUndefined(this.availableSpaceInBuggyInPicks) || this.availableSpaceInBuggyInPicks === this.buggyLengthMaxInPicks) {
        availableSpaceInBuggy = this.buggyLengthMaxInPicks;
      } else if (this.availableSpaceInBuggyInPicks > 0) {
        availableSpaceInBuggy = this.availableSpaceInBuggyInPicks + this.getLongestPathLengthInPicks(this.productionSchedule);
      } else {
        availableSpaceInBuggy = this.getLongestPathLengthInPicks(this.productionSchedule);
      }

      const widthPerPixel = availableWindowSpace / availableSpaceInBuggy;

      this.productionSchedulePath?.pathGroups?.forEach((productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup, index: number) => {
        const widthOfPathItem = (this.widthOfPathItems[index] =
          (productionScheduleItemInPathGroup?.technicalDimensions?.heightInPicks + productionScheduleItemInPathGroup.getTechnicalCutbarsLengthInNumberOfPicks()) *
            productionScheduleItemInPathGroup?.quantity *
            widthPerPixel -
          this.TOTAL_TO_DEDUCT_FROM_PATH_ITEM_FOR_PADDING_AND_GAPS);

        if (widthOfPathItem < this.MINIMAL_WIDTH_OF_PATH_BAR_ITEM) {
          this.widthOfPathItems[index] = this.MINIMAL_WIDTH_OF_PATH_BAR_ITEM;
        } else {
          this.widthOfPathItems[index] = widthOfPathItem;
        }
      });
    }
  }

  private getLongestPathLengthInPicks(productionSchedule: ProductionSchedule): number {
    const productionSchedulePathLenths =
      productionSchedule?.productionSchedulePathsOfColoredYarnSets
        .flatMap((productionSchedulePathsOfColoredYarnSet: ProductionSchedulePathsOfColoredYarnSet) => productionSchedulePathsOfColoredYarnSet?.productionSchedulePaths ?? [])
        .map((productionSchedulePath: ProductionSchedulePath) => this.getTotalHeightsOfProductionSchedulePath(productionSchedulePath).totalHeightInPicks ?? 0) ?? [];

    return Math.max(0, ...productionSchedulePathLenths);
  }

  private getTotalHeightsOfProductionSchedulePath(productionSchedulePath: ProductionSchedulePath): {totalHeightInPicks: number; totalHeightInMM: number} {
    let totalHeightInPicks = 0;
    let totalHeightInMM = 0;

    productionSchedulePath?.pathGroups?.forEach((item: ProductionScheduleItemInPathGroup) => {
      totalHeightInPicks = totalHeightInPicks + (item?.technicalDimensions?.heightInPicks + item?.getTechnicalCutbarsLengthInNumberOfPicks()) * item?.quantity;
      totalHeightInMM = totalHeightInMM + (item?.commercialDimensionsInMM?.heightInMM + item?.getCommercialCutbarsLengthInMM()) * item?.quantity;
    });

    return {totalHeightInPicks: totalHeightInPicks ?? 0, totalHeightInMM: totalHeightInMM ?? 0};
  }

  private editProductionScheduleItem(productionScheduleItemInPathGroup: ProductionScheduleItemInPathGroup): void {
    this.productionScheduleItem = productionScheduleItemInPathGroup;

    this.productionSchedulePlan.editProductionScheduleItem(
      productionScheduleItemInPathGroup,
      this.productionScheduleItemIndexInPath,
      this.productionSchedulePathIndex,
      this.productionScheduleColoredYarnSet.startDent
    );
  }

  private removeProductionScheduleItemInPathGroup(): void {
    const productionSchedulePathOfColoredYarnSet = this.productionSchedule.getProductionSchedulePathsOfColoredYarnSetAtStartDent(this.productionScheduleColoredYarnSet.startDent);
    const productionSchedulePath = productionSchedulePathOfColoredYarnSet.productionSchedulePaths[this.productionSchedulePathIndex];

    this.productionSchedulePlan.removeProductionScheduleItem(
      this.productionScheduleItem.uuid,
      productionSchedulePath.pathNumber,
      productionSchedulePathOfColoredYarnSet.productionScheduleColoredYarnSet.startDent,
      true
    );
  }

  private getOrderlineProperty(orderLineId: number): ProductionScheduleOrderLine {
    if (orderLineId !== null && this.productionSchedule && !AssertionUtils.isEmpty(this.productionSchedule.orderLines)) {
      const foundOrderLine = this.productionSchedule.orderLines.find((orderLine: ProductionScheduleOrderLine) => orderLine.id === orderLineId);

      if (foundOrderLine) {
        return foundOrderLine;
      }
    }

    return null;
  }
}
